import { useQuery, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { distributeStockImages } from "@/lib/cafeImages";

// Module-level registry of evictors from any mounted useAccumulatedCoverPhotos.
// Lets external mutations (cover-photo setters) clear a cafe id from the
// per-instance settled/accumulator refs so the next render refetches it.
type CoverEvictor = (cafeId: string) => void;
const coverEvictors = new Set<CoverEvictor>();

/**
 * Call after mutating is_cover for a cafe. Evicts the cafe id from every
 * mounted useAccumulatedCoverPhotos instance and invalidates the relevant
 * React Query caches so the next render shows the new cover.
 */
export function invalidateCafeCoverPhoto(queryClient: QueryClient, cafeId: string) {
  for (const evict of coverEvictors) {
    try { evict(cafeId); } catch { /* ignore */ }
  }
  queryClient.invalidateQueries({ queryKey: ["cafe-cover-photos-accum-batch"] });
  queryClient.invalidateQueries({ queryKey: ["cafe-cover-photos"] });
  queryClient.invalidateQueries({ queryKey: ["cafe-cover-photos-batch"] });
}

interface CoverPhotoResult {
  cafe_id: string;
  photo_url: string;
}

/**
 * Fetches the best cover photo for a list of cafes.
 * Priority: is_cover photo > first approved photo
 * Returns a Map of cafe_id -> photo_url for easy lookup
 */
export function useCafeCoverPhotos(cafeIds: string[]) {
  return useQuery({
    queryKey: ["cafe-cover-photos", cafeIds.sort().join(",")],
    queryFn: async () => {
      if (cafeIds.length === 0) return new Map<string, string>();

      // Batch into chunks of 50, fetch all in parallel
      const BATCH_SIZE = 50;
      const batches: string[][] = [];
      for (let i = 0; i < cafeIds.length; i += BATCH_SIZE) {
        batches.push(cafeIds.slice(i, i + BATCH_SIZE));
      }
      const results = await Promise.all(
        batches.map(batch =>
          supabase
            .from("cafe_photos")
            .select("cafe_id, photo_url, is_cover, quality_score")
            .in("cafe_id", batch)
            .eq("is_approved", true)
            .order("is_cover", { ascending: false })
            .order("quality_score", { ascending: false, nullsFirst: false })
            .order("created_at", { ascending: true })
        )
      );
      const photoMap = new Map<string, string>();
      for (const { data, error } of results) {
        if (error) throw error;
        for (const photo of data || []) {
          if (!photoMap.has(photo.cafe_id)) {
            photoMap.set(photo.cafe_id, photo.photo_url);
          }
        }
      }
      return photoMap;
    },
    enabled: cafeIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
}

type CoverAccumBatch = { map: Map<string, string>; batchIds: string[] };

/**
 * Like useCafeCoverPhotos, but keeps a stable Map across "Load more" so the query
 * key only covers newly visible IDs — existing cards keep their cached URLs while
 * new batches load.
 */
export function useAccumulatedCoverPhotos(cafeIds: string[]) {
  const accumulatorRef = useRef(new Map<string, string>());
  const settledIdsRef = useRef(new Set<string>());
  const mergedBatchKeysRef = useRef(new Set<string>());
  const [version, setVersion] = useState(0);

  const newIds = useMemo(() => {
    void version;
    return cafeIds.filter((id) => !settledIdsRef.current.has(id));
  }, [cafeIds, version]);

  // Register an evictor so external mutations can drop a cafeId from cache
  useEffect(() => {
    const evict: CoverEvictor = (cafeId) => {
      let changed = false;
      if (settledIdsRef.current.delete(cafeId)) changed = true;
      if (accumulatorRef.current.delete(cafeId)) changed = true;
      // Drop any merged-batch keys that included this id so its batch can refetch
      for (const key of mergedBatchKeysRef.current) {
        if (key.split(",").includes(cafeId)) {
          mergedBatchKeysRef.current.delete(key);
          changed = true;
        }
      }
      if (changed) setVersion((v) => v + 1);
    };
    coverEvictors.add(evict);
    return () => { coverEvictors.delete(evict); };
  }, []);

  const batchKeyStr = useMemo(() => [...newIds].sort().join(","), [newIds]);

  const query = useQuery({
    queryKey: ["cafe-cover-photos-accum-batch", batchKeyStr],
    queryFn: async (): Promise<CoverAccumBatch> => {
      const batchIds = [...newIds].sort();
      if (batchIds.length === 0) return { map: new Map(), batchIds: [] };

      const BATCH_SIZE = 50;
      const batches: string[][] = [];
      for (let i = 0; i < batchIds.length; i += BATCH_SIZE) {
        batches.push(batchIds.slice(i, i + BATCH_SIZE));
      }
      const results = await Promise.all(
        batches.map((batch) =>
          supabase
            .from("cafe_photos")
            .select("cafe_id, photo_url, is_cover, quality_score")
            .in("cafe_id", batch)
            .eq("is_approved", true)
            .order("is_cover", { ascending: false })
            .order("quality_score", { ascending: false, nullsFirst: false })
            .order("created_at", { ascending: true })
        )
      );
      const photoMap = new Map<string, string>();
      for (const { data, error } of results) {
        if (error) throw error;
        for (const photo of data || []) {
          if (!photoMap.has(photo.cafe_id)) {
            photoMap.set(photo.cafe_id, photo.photo_url);
          }
        }
      }
      return { map: photoMap, batchIds };
    },
    enabled: newIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (!query.isSuccess || !query.data || newIds.length === 0) return;
    if (mergedBatchKeysRef.current.has(batchKeyStr)) return;
    mergedBatchKeysRef.current.add(batchKeyStr);

    const { map, batchIds } = query.data;
    for (const [k, v] of map) {
      accumulatorRef.current.set(k, v);
    }
    for (const id of batchIds) {
      settledIdsRef.current.add(id);
    }
    setVersion((v) => v + 1);
  }, [query.isSuccess, query.data, batchKeyStr, newIds.length]);

  const data = useMemo(() => {
    void version;
    return new Map(accumulatorRef.current);
  }, [version]);

  return { data };
}

/**
 * Hook that provides distributed stock images for cafes without user photos.
 * Ensures no stock image repeats within a 3-item window when displayed in a list.
 */
export function useDistributedStockImages(
  cafes: Array<{ id: string; cover_image_url?: string | null; placeholder_index?: number | null }>,
  userPhotoMap?: Map<string, string>
) {
  return useMemo(() => {
    return distributeStockImages(cafes, userPhotoMap);
  }, [cafes, userPhotoMap]);
}

/**
 * Get the display image URL for a cafe, preferring user photos over stock
 */
export function getCafeDisplayImage(
  cafe: { id: string; cover_image_url?: string | null },
  userPhotoMap: Map<string, string> | undefined,
  stockPhotoMap: Map<string, string> | undefined,
  fallbackUrl: string | null
): { url: string; isUserPhoto: boolean } {
  // Priority 1: User-submitted cover photo
  const userPhoto = userPhotoMap?.get(cafe.id);
  if (userPhoto) {
    return { url: userPhoto, isUserPhoto: true };
  }

  // Priority 2: Admin-set cover image
  if (cafe.cover_image_url) {
    return { url: cafe.cover_image_url, isUserPhoto: false };
  }

  // Priority 3: Distributed stock image (prevents adjacent duplicates)
  const stockPhoto = stockPhotoMap?.get(cafe.id);
  if (stockPhoto) {
    return { url: stockPhoto, isUserPhoto: false };
  }

  // Priority 4: Fallback stock/placeholder image
  return { url: fallbackUrl || "", isUserPhoto: false };
}
